import { Outlet } from 'react-router-dom';

import { lazyWithRetry } from '@/shared/lib/lazyWithRetry';

import { ROUTES } from '@/shared/constants';

import {
  ActorCastingAccessRedirect,
  ActorProjectAccessRedirect
} from '@/features/casting';

import { LoggedInUserComposition } from '../di/compositions/LoggedInUser';
import { sentryCreateBrowserRouter } from '../sentry';

const DirectorActorInfoPage = lazyWithRetry(
  () => import('@/pages/director/actor-info')
);
const DirectorCastingInfoPage = lazyWithRetry(
  () => import('@/pages/director/casting-info')
);
const DirectorMainPage = lazyWithRetry(() => import('@/pages/director/main'));
const DirectorCreateProjectPage = lazyWithRetry(
  () => import('@/pages/director/project-create')
);
const DirectorEditProjectPage = lazyWithRetry(
  () => import('@/pages/director/project-edit')
);
const DirectorProjectInfoPage = lazyWithRetry(
  () => import('@/pages/director/project-info')
);
const DirectorCreateCastingPage = lazyWithRetry(
  () => import('@/pages/director/casting-create')
);
const DirectorEditCastingPage = lazyWithRetry(
  () => import('@/pages/director/casting-edit')
);
const DirectorProjectsPage = lazyWithRetry(
  () => import('@/pages/director/projects')
);

const DirectorFavoritesPage = lazyWithRetry(
  () => import('@/pages/director/favorites')
);

const DirectorActorsPage = lazyWithRetry(
  () => import('@/pages/director/actors')
);

const DirectorActorCardsPage = lazyWithRetry(
  () => import('@/pages/director/actor-cards')
);

const DirectorFilmOffersPage = lazyWithRetry(
  () => import('@/pages/director/film-offers')
);

const DirectorFilmOfferPage = lazyWithRetry(
  () => import('@/pages/director/film-offer')
);

const NotFoundPage = lazyWithRetry(() => import('@/pages/not-found'));
const EmailVerificationPage = lazyWithRetry(
  () => import('@/pages/email-verification')
);

const DirectorSettingsPage = lazyWithRetry(
  () => import('@/pages/director/settings')
);

const DirectorSupportPage = lazyWithRetry(
  () => import('@/pages/director/support')
);

const DirectorProfilePage = lazyWithRetry(
  () => import('@/pages/director/profile')
);

const DirectorNotifications = lazyWithRetry(
  () => import('@/pages/director/notifications')
);

const DirectorResponsePage = lazyWithRetry(
  () => import('@/pages/director/response')
);

const ResponsesListPage = lazyWithRetry(
  () => import('@/pages/director/responses')
);
const PhoneVerificationPage = lazyWithRetry(
  () => import('@/pages/phone-verification')
);
const PublicVideoPage = lazyWithRetry(() => import('@/pages/public-video'));

const routes = [
  {
    path: ROUTES.public_video,
    element: <PublicVideoPage />
  },
  {
    element: (
      <LoggedInUserComposition>
        <Outlet />
      </LoggedInUserComposition>
    ),
    children: [
      {
        path: '/',
        element: <DirectorMainPage />
      },
      {
        path: '/actors',
        handle: {
          crumb: { name: 'Поиск актёров' }
        },
        children: [
          {
            index: true,
            element: <DirectorActorsPage />
          },
          {
            path: ':id',
            handle: {
              crumb: { name: 'Актёр' }
            },
            element: <DirectorActorInfoPage />
          }
        ]
      },
      {
        path: 'favorites',
        children: [
          {
            index: true,
            element: <DirectorFavoritesPage />
          }
        ]
      },
      {
        path: 'actor-registry',
        children: [
          {
            index: true,
            element: <DirectorActorCardsPage />
          }
        ]
      },
      {
        path: 'settings',
        children: [
          {
            index: true,
            element: <DirectorSettingsPage />
          }
        ]
      },
      {
        path: '/projects',
        handle: {
          crumb: { name: 'Проекты' }
        },
        children: [
          {
            index: true,
            element: <DirectorProjectsPage />
          },
          {
            path: 'new-project',
            element: <DirectorCreateProjectPage />
          },
          {
            path: 'edit/:projectId',
            element: <DirectorEditProjectPage />
          },
          {
            path: ':projectId',
            handle: {
              crumb: { name: 'Проект' }
            },
            children: [
              {
                index: true,
                element: <DirectorProjectInfoPage />
              },
              {
                path: 'castings/:castingId',
                children: [
                  {
                    index: true,
                    element: <DirectorCastingInfoPage />
                  },
                  {
                    path: 'responses/:responseId',
                    element: <DirectorResponsePage />
                  },
                  {
                    path: 'invitations/:invitationId',
                    element: <DirectorResponsePage />
                  }
                ]
              },
              {
                path: 'new-casting',
                element: <DirectorCreateCastingPage />
              },
              {
                path: 'castings/edit/:castingId',
                element: <DirectorEditCastingPage />
              }
            ]
          }
        ]
      },
      {
        path: 'notifications',
        children: [
          {
            index: true,
            element: <DirectorNotifications />
          }
        ]
      },
      {
        path: 'support',
        children: [
          {
            index: true,
            element: <DirectorSupportPage />
          }
        ]
      },
      {
        path: '/profile',
        element: <DirectorProfilePage />
      },
      {
        path: 'responses',
        children: [
          {
            index: true,
            element: <ResponsesListPage />
          },
          {
            path: 'castings/:castingId/responses/:responseId',
            element: <DirectorResponsePage />
          },
          {
            path: 'castings/:castingId/invitations/:invitationId',
            element: <DirectorResponsePage />
          }
        ]
      },
      {
        path: 'events/projects/:projectId/castings/:castingId',
        element: <ActorCastingAccessRedirect />
      },
      {
        path: 'events/projects/:projectId',
        element: <ActorProjectAccessRedirect />
      },
      {
        path: ROUTES.change_email,
        element: <EmailVerificationPage />
      },
      {
        path: ROUTES.change_phone,
        element: <PhoneVerificationPage />
      },
      {
        path: '/film-offers',
        handle: { crumb: { name: 'Фильмы с вашим участием' } },
        children: [
          { index: true, element: <DirectorFilmOffersPage /> },
          {
            path: ':id',
            handle: { crumb: { name: 'Предложение' } },
            element: <DirectorFilmOfferPage />
          }
        ]
      },
      {
        path: '/*',
        element: <NotFoundPage />
      }
    ]
  }
];

export const directorRouter = sentryCreateBrowserRouter(routes);
